import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Header } from '../../components/Header';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { UpsellProgressBar } from '../../components/UpsellProgressBar';
import { PurchaseSuccessModal } from '../../components/PurchaseSuccessModal';
import { savePurchaseProduct } from '../../../lib/use-cases/get-purchase';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { buyUpsell } from '../../../lib/use-cases/buy-upsell';
import {
    FULL_DIGITAL_SAFETY_GUIDE_PRODUCT,
    HOW_TO_SHOP_ONLINE_SAFELY_1999_PRODUCT,
    SCAM_PROOF_POCKET_GUIDE_1999_PRODUCT
} from '../../../lib/shared/products';
import { UPSELL_STEPS } from '../../../lib/shared/upsell-steps';
import { useUpsellStep } from '../../shared/use-upsell-flow';
import { BUNDLE_STEP } from '../../../lib/shared/upsell-flow-steps';
import bundleIconSrc from '../../assets/images/upsell/bundle/bundle.png';
import shoppingIconSrc from '../../assets/images/upsell/bundle/shopping.png';
import scamIconSrc from '../../assets/images/upsell/bundle/scam.png';
import styles from './UpsellBundlePage.css';

const cx = classnames.bind(styles);

const OPTIONS = [
    {
        id: 'bundle',
        productId: FULL_DIGITAL_SAFETY_GUIDE_PRODUCT,
        name: 'All-in-One Privacy Guide',
        price: '26.99',
        oldPrice: '39.98',
        priceSuffix: 'only!',
        discount: 32,
        iconSrc: bundleIconSrc
    },
    {
        id: 'shopping',
        productId: HOW_TO_SHOP_ONLINE_SAFELY_1999_PRODUCT,
        name: 'Online Shopping Safety Guide',
        price: '19.99',
        iconSrc: `${shoppingIconSrc}?v=2`
    },
    {
        id: 'scam',
        productId: SCAM_PROOF_POCKET_GUIDE_1999_PRODUCT,
        name: 'Scam Protection Guide',
        price: '19.99',
        iconSrc: `${scamIconSrc}?v=2`
    }
];

const BENEFITS = [
    'One-time payment',
    'Instant delivery to your email',
    'Lifetime access',
    'Written in plain English, no tech jargon'
];

function UpsellBundlePage() {
    const { subscriptionId, nextUrl, withSuccessModal } = useUpsellStep(BUNDLE_STEP);
    const [selectedId, setSelectedId] = useState(OPTIONS[0].id);
    const [isPending, setIsPending] = useState(false);
    const selectedOption = OPTIONS.find(option => option.id === selectedId);

    useEffect(() => {
        logEvent(EVENT_TYPES.UPSELL_BUNDLE_PAGE_VISITED);
    }, []);

    const onSelectProduct = option => {
        setSelectedId(option.id);
        logEvent(EVENT_TYPES.UPSELL_BUNDLE_PRODUCT_CLICKED, { product: option.productId });
    };

    const onBuy = async () => {
        setIsPending(true);

        logEvent(EVENT_TYPES.UPSELL_PAGE_CLICK_PAY, { container: 'body', 'upsell_type': 'bundle', product: selectedOption.productId });

        await buyUpsell(subscriptionId, selectedOption.productId);

        savePurchaseProduct('upsell');
        window.location.replace(nextUrl);
    };

    return (
        <>
            {withSuccessModal && <PurchaseSuccessModal />}
            <Header
                url={window.location.pathname}
                className={styles.header}
                finished={false}
                renderLeftNavigation={() => (
                    <Button
                        label='SKIP'
                        type='secondary'
                        iconRight={{ name: 'chevronRight' }}
                        link={{ to: nextUrl }}
                        className={styles.skipButton}
                        onClick={() => logEvent(EVENT_TYPES.UPSELL_PAGE_SKIPPED, { 'target_click_block': 'header' })}
                    />
                )}
            />
            <UpsellProgressBar currentStep={UPSELL_STEPS.ADD_UPGRADE} />
            <main className={styles.main} id='upsell'>
                <h1 className={styles.title}>Exclusive Sign-up Offer</h1>
                <ul className={styles.options}>
                    {OPTIONS.map(option => {
                        const isActive = option.id === selectedId;

                        return (
                            <li key={option.id}>
                                <label className={cx('card', { active: isActive })}>
                                    <input
                                        type='radio'
                                        name='upsell-bundle-option'
                                        value={option.id}
                                        checked={isActive}
                                        onChange={() => onSelectProduct(option)}
                                        className={styles.input}
                                    />
                                    <div className={styles.iconSide}>
                                        <img
                                            src={option.iconSrc}
                                            alt=''
                                            className={styles.icon}
                                        />
                                    </div>
                                    <div className={styles.contentSide}>
                                        <div className={styles.text}>
                                            <p className={styles.name}>{option.name}</p>
                                            <p className={styles.priceRow}>
                                                {Boolean(option.oldPrice) && <s className={styles.oldPrice}>${option.oldPrice}</s>}
                                                {Boolean(option.oldPrice) && <Icon name='forward' className={styles.arrow} />}
                                                <span className={cx('price', { withoutDiscount: !option.discount })}>
                                                    ${option.price}{option.priceSuffix ? ` ${option.priceSuffix}` : ''}
                                                </span>
                                                {Boolean(option.discount) && <span className={styles.discount}>{option.discount}% OFF</span>}
                                            </p>
                                        </div>
                                        <span className={styles.radio} aria-hidden='true' />
                                    </div>
                                </label>
                            </li>
                        );
                    })}
                </ul>
                <ul className={styles.benefits}>
                    {BENEFITS.map(benefit => (
                        <li key={benefit}>
                            <Icon name='checkItem' />
                            <span>{benefit}</span>
                        </li>
                    ))}
                </ul>
                <p className={styles.disclaimer}>
                    By clicking Buy Now, you agree that you will be automatically charged using the same billing method you provided at the checkout.
                </p>
                <Button
                    label='buy now'
                    onClick={onBuy}
                    isLoading={isPending}
                    className={styles.button}
                />
                <Button
                    label='Skip'
                    type='secondary'
                    link={{ to: nextUrl }}
                    className={styles.skipButtonMain}
                    onClick={() => logEvent(EVENT_TYPES.UPSELL_PAGE_SKIPPED, { 'target_click_block': 'body' })}
                />
            </main>
        </>
    );
}

export {
    UpsellBundlePage
};
